import AppointmensState from '../../consts/AppointmentsState';

/**
 * Texto das entradas do histórico (quem cadastrou/alterou e quando) do
 * paciente e do agendamento. As entradas vêm de patients/history.php e
 * appointments/history.php; os rótulos de profissional, procedimento, sala e
 * paciente vêm prontos em "labels".
 */

const pad = (n) => String(n).padStart(2, '0');

/** 'YYYY-MM-DD HH:MM:SS' -> 'DD/MM/YYYY HH:MM'; só a data quando dateOnly */
export function formatHistoryDate(value, dateOnly = false) {
  if (!value) return '—';
  const [datePart, timePart = ''] = String(value).split(' ');
  const [year, month, day] = datePart.split('-');
  if (!year || !month || !day) return value;
  const date = `${day}/${month}/${year}`;
  if (dateOnly || !timePart) return date;
  return `${date} ${timePart.slice(0, 5)}`;
}

function formatPrice(value) {
  const number = Number(value);
  if (value === null || value === undefined || value === '' || Number.isNaN(number)) return '—';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(number);
}

/** '01:30:00' -> '1h30'; '00:45:00' -> '45 min' */
function formatDuration(value) {
  if (!value) return '—';
  const [h = '0', m = '0'] = String(value).split(':');
  const hours = parseInt(h, 10) || 0;
  const minutes = parseInt(m, 10) || 0;
  if (hours === 0) return `${minutes} min`;
  return minutes ? `${hours}h${pad(minutes)}` : `${hours}h`;
}

function labelFrom(map, id, empty) {
  if (id === null || id === undefined || id === '' || String(id) === '-1') return empty;
  return (map && map[String(id)]) || `#${id}`;
}

/** Lista de ids de procedimento -> 'Axila + Virilha + Pernas' */
function procedureListLabel(ids, labels) {
  const list = Array.isArray(ids) ? ids : [];
  if (!list.length) return 'Sem procedimento';
  return list.map((id) => labelFrom(labels.procedures, id, 'Sem procedimento')).join(' + ');
}

const APPOINTMENT_FIELDS = {
  status: { label: 'Status', format: (v) => (v ? AppointmensState.GetLabel(v) : '—') },
  date: { label: 'Data', format: (v) => formatHistoryDate(v) },
  resource: { label: 'Profissional', format: (v, labels) => labelFrom(labels.resources, v, 'Sem profissional') },
  procedure: { label: 'Procedimento', format: (v, labels) => labelFrom(labels.procedures, v, 'Sem procedimento') },
  procedures: { label: 'Procedimentos', format: (v, labels) => procedureListLabel(v, labels) },
  roomId: { label: 'Sala', format: (v, labels) => labelFrom(labels.rooms, v, 'Sem sala') },
  attendance_mode: { label: 'Modalidade', format: (v) => (v === 'online' ? 'Online' : 'Presencial') },
  // O histórico guarda só se o link foi informado, nunca a URL
  meeting_url: { label: 'Link da videochamada', format: (v) => (v ? 'Informado' : 'Não informado') },
  billing_type: { label: 'Cobertura', format: (v) => (v === 'insurance' ? 'Convênio' : 'Particular') },
  // Carteirinha: o histórico só diz se havia uma, sem expor o número
  patient_insurance_id: { label: 'Carteirinha', format: (v) => (v ? 'Informada' : 'Nenhuma') },
  duration: { label: 'Duração', format: (v) => formatDuration(v) },
  price: { label: 'Valor', format: (v) => formatPrice(v) },
  patient: { label: 'Paciente', format: (v, labels) => labelFrom(labels.patients, v, 'Sem paciente') },
};

// Colunas do cadastro agrupadas como a pessoa as vê no formulário
const PATIENT_FIELD_GROUPS = {
  status: 'Situação',
  name: 'Nome',
  cpf: 'CPF',
  birthday: 'Data de nascimento',
  gender: 'Gênero',
  countryCode: 'Telefone',
  phone: 'Telefone',
  email: 'E-mail',
  cep: 'Endereço',
  address: 'Endereço',
  number: 'Endereço',
  complement: 'Endereço',
  district: 'Endereço',
  city: 'Endereço',
  state: 'Endereço',
  origin: 'Origem',
  origin_channel_id: 'Origem',
  origin_campaign_id: 'Origem',
  financial_responsible_name: 'Responsável financeiro',
  financial_responsible_document: 'Responsável financeiro',
  financial_responsible_email: 'Responsável financeiro',
  financial_responsible_phone: 'Responsável financeiro',
  financial_responsible_relationship: 'Responsável financeiro',
};

function describeChanges(changes, labels) {
  return Object.entries(changes || {})
    .filter(([field]) => APPOINTMENT_FIELDS[field])
    .map(([field, change]) => {
      const { label, format } = APPOINTMENT_FIELDS[field];
      return `${label}: ${format(change.from, labels)} → ${format(change.to, labels)}`;
    });
}

function titleFor(entry) {
  const { action, details = {} } = entry;
  const byPatientLink = details.source === 'patient_link';

  switch (action) {
    case 'patient.create':
      return 'Cadastrou o paciente';
    case 'patient.update':
      return 'Alterou o cadastro';
    case 'appointment.create':
      if (byPatientLink) return 'Reagendou pelo link de confirmação';
      if (details.source === 'recurring') return 'Criou o agendamento (recorrência)';
      return details.status === 'blocked' ? 'Criou o bloqueio' : 'Criou o agendamento';
    case 'appointment.update':
      if (details.source === 'patient_whatsapp') return 'Confirmou pelo WhatsApp';
      return byPatientLink ? 'Respondeu pelo link de confirmação' : 'Alterou o agendamento';
    case 'appointment.delete':
      return 'Excluiu o bloqueio';
    case 'patient.appointment.delete':
      return 'Excluiu um agendamento';
    // Pagamento só com sessões de pacote: valor 0, forma de pagamento 'credits'
    case 'appointment.payment.create':
      return details.paymentMethod === 'credits'
        ? 'Registrou pagamento com sessões de pacote'
        : `Registrou pagamento de ${formatPrice(details.price)}`;
    case 'appointment.payment.delete':
      return details.paymentMethod === 'credits'
        ? 'Excluiu pagamento com sessões de pacote'
        : `Excluiu pagamento de ${formatPrice(details.price)}`;
    case 'appointment.payment.move':
      return details.direction === 'in'
        ? `Pagamento veio do agendamento de ${formatHistoryDate(details.otherDate)}`
        : `Pagamento passou para o agendamento de ${formatHistoryDate(details.otherDate)}`;
    // Etapas do atendimento (checklist dentro do status)
    case 'appointment.step.check':
      return `Marcou a etapa "${details.stepName || ''}"`;
    case 'appointment.step.uncheck':
      return `Desmarcou a etapa "${details.stepName || ''}"`;
    default:
      return action;
  }
}

function linesFor(entry, labels) {
  const { action, details = {} } = entry;

  if (action === 'patient.update') {
    const groups = [...new Set((details.fields || []).map((f) => PATIENT_FIELD_GROUPS[f] || f))];
    return groups.length ? [`Campos: ${groups.join(', ')}`] : [];
  }
  if (action === 'appointment.update') {
    return describeChanges(details.changes, labels);
  }
  if (action === 'appointment.create' && !entry.legacy && details.status !== 'blocked') {
    const lines = [];
    if (details.resource && String(details.resource) !== '-1') {
      lines.push(`Profissional: ${labelFrom(labels.resources, details.resource)}`);
    }
    if (Array.isArray(details.procedures) && details.procedures.length > 1) {
      lines.push(`Procedimentos: ${procedureListLabel(details.procedures, labels)}`);
    } else if (details.procedure) {
      lines.push(`Procedimento: ${labelFrom(labels.procedures, details.procedure)}`);
    }
    return lines;
  }
  if (action === 'patient.appointment.delete' || action === 'appointment.delete') {
    return [`Era em ${formatHistoryDate(details.date)} (${AppointmensState.GetLabel(details.status)})`];
  }
  if (action === 'appointment.payment.create') {
    const sessions = Array.isArray(details.sessions_used) ? details.sessions_used : [];
    if (!sessions.length) return [];
    const names = sessions.map((s) => labelFrom(labels.procedures, s.procedure_id, 'Sem procedimento'));
    return [`${sessionsLabel(sessions.length)} de pacote: ${names.join(', ')}`];
  }
  if (action === 'appointment.payment.delete') {
    const released = Number(details.sessions_released) || 0;
    return released > 0 ? [`${sessionsLabel(released)} devolvida${released === 1 ? '' : 's'} ao pacote`] : [];
  }
  return [];
}

/** 1 -> '1 sessão'; 3 -> '3 sessões' */
function sessionsLabel(count) {
  return `${count} ${count === 1 ? 'sessão' : 'sessões'}`;
}

function authorFor(entry) {
  if (entry.userName) return entry.userName;
  if (['patient_link', 'patient_whatsapp'].includes(entry.details?.source)) return 'Paciente';
  if (entry.legacy) return 'Autor não registrado';
  if (entry.userId === null || entry.userId === undefined) return 'Sistema';
  return `Usuário #${entry.userId}`;
}

/**
 * Descreve uma entrada para exibição.
 * @param {Object} entry - entrada do endpoint
 * @param {Object} labels - rótulos resolvidos pelo endpoint
 * @param {boolean} showAppointment - true na ficha do paciente (diz de qual agendamento)
 * @returns {{key: string, title: string, lines: string[], author: string, when: string, context: string|null}}
 */
export function describeHistoryEntry(entry, labels = {}, showAppointment = false) {
  const isAppointmentEntry = entry.targetType === 'appointment';
  const appointmentDate = entry.appointmentDate || entry.details?.date;

  return {
    key: entry.id ? `log-${entry.id}` : `legacy-${entry.action}-${entry.targetId}`,
    title: titleFor(entry),
    lines: linesFor(entry, labels),
    author: authorFor(entry),
    when: formatHistoryDate(entry.createdAt, entry.legacy),
    context: showAppointment && isAppointmentEntry && appointmentDate
      ? `Agendamento de ${formatHistoryDate(appointmentDate)}`
      : null,
  };
}
